import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, MessageCircle, BookOpen, Heart, ArrowRight, FlaskConical, Bot, LogOut, User as UserIcon, CreditCard, LayoutDashboard, Star } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Seo from "@/components/Seo";
import heroPortrait from "@/assets/hero-portrait.webp";
import flatlay from "@/assets/flatlay-skincare.webp";
import petals from "@/assets/petals-macro.webp";
import serum from "@/assets/serum-texture.webp";
import glow from "@/assets/glow-portrait.webp";
import stickerStar from "@/assets/sticker-star.webp";
import stickerHeart from "@/assets/sticker-heart.webp";
import SocialShareRow from "@/components/SocialShareRow";

const glowieQuotes = [
  {
    q: "your barrier is begging. let's pull back the actives for 3 nights.",
    ctx: "Glowie, on a tight skin day",
    rotate: "sm:rotate-[-2deg]",
  },
  {
    q: "niacinamide AM, retinal PM, sunscreen on top. 3 steps, that's the whole glow.",
    ctx: "Glowie, building your routine",
    rotate: "sm:rotate-[1.5deg]",
  },
  {
    q: "want a natural option? OSEA Undaria does what your retinol promised 🌿",
    ctx: "Glowie, on synthetic vs natural",
    rotate: "sm:rotate-[-1deg]",
  },
  {
    q: "fragrance plus acids on dry skin is why your forehead is upset.",
    ctx: "Glowie, decoding your shelf",
    rotate: "sm:rotate-[2deg]",
  },
  {
    q: "centella beats harsh acne creams when you're red and angry. patch test first.",
    ctx: "Glowie, on calming flare-ups",
    rotate: "sm:rotate-[-1.5deg]",
  },
  {
    q: "spf is non-negotiable. even on cloudy days. especially on cloudy days. ☀️",
    ctx: "Glowie, every single morning",
    rotate: "sm:rotate-[1deg]",
  },
];

const features = [
  {
    icon: MessageCircle,
    eyebrow: "01 / Match",
    title: "Products picked for your skin type",
    body: "Tell the AI your skin type, concerns, and budget. It recommends real products that actually fit you, no generic lists.",
    accent: petals,
  },
  {
    icon: FlaskConical,
    eyebrow: "02 / Formulate",
    title: "Make your own products",
    body: "The AI helps you formulate custom serums, masks, and oils at home, with exact ratios and safety notes.",
    accent: serum,
  },
  {
    icon: Sparkles,
    eyebrow: "03 / Solve",
    title: "Targeted help for your problems",
    body: "Breakouts, redness, hyperpigmentation, dryness. Describe what is happening and get a step-by-step plan to fix it.",
    accent: flatlay,
  },
  {
    icon: BookOpen,
    eyebrow: "04 / Learn",
    title: "Decode the back of the bottle",
    body: "An ingredient library that explains what each one does, who it works for, and what to pair it with.",
    accent: glow,
  },
];

// TODO: Swap these placeholder testimonials for real quotes when available.
const testimonials = [
  {
    q: "Finally, skincare advice that doesn't sound like a robot reading from a textbook.",
    name: "Sarah",
    desc: "sensitive skin",
  },
  {
    q: "I've been doing skincare for years and Glowie still caught something I missed about my routine.",
    name: "Jess",
    desc: "dry skin + rosacea",
  },
  {
    q: "Made my first DIY serum from a Glowie formula. My skin is so much happier.",
    name: "Priya",
    desc: "oily / acne-prone",
  },
];

const painPoints = [
  "Spent $80 on a serum that broke you out",
  "Followed three TikTok routines and made your skin worse",
  "Got a quiz result that handed you a generic \"combination skin\" plan",
  "Have 12 products on your shelf and still don't know which work together",
];

const Index = () => {
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const displayName =
    profile?.display_name?.trim() ||
    profile?.username?.trim() ||
    user?.email?.split("@")[0] ||
    "you";
  const initial = (displayName[0] || "?").toUpperCase();
  // Send signed-in users to the most useful place based on their quiz state.
  // If they've finished the quiz, their routine is the payoff page.
  // If not, drop them into the quiz so the "Open app" CTA actually delivers.
  const quizDone = Boolean(profile?.quiz_completed_at);
  const primaryAppPath = user ? (quizDone ? "/app/routine" : "/quiz") : "/auth?mode=signup";
  const ctaPath = user ? "/app/chat" : "/auth?mode=signup";
  const ctaLabel = user ? "Open chat" : "Try it free";

  // Sticky mobile CTA bar: hidden while inside the hero, hidden once footer is visible.
  const heroRef = useRef<HTMLElement>(null);
  const footerRef = useRef<HTMLElement>(null);
  const [pastHero, setPastHero] = useState(false);
  const [atFooter, setAtFooter] = useState(false);
  useEffect(() => {
    const heroObs = new IntersectionObserver(
      ([e]) => setPastHero(!e.isIntersecting),
      { threshold: 0, rootMargin: "-80px 0px 0px 0px" },
    );
    if (heroRef.current) heroObs.observe(heroRef.current);
    const footObs = new IntersectionObserver(
      ([e]) => setAtFooter(e.isIntersecting),
      { threshold: 0 },
    );
    if (footerRef.current) footObs.observe(footerRef.current);
    return () => {
      heroObs.disconnect();
      footObs.disconnect();
    };
  }, []);
  const showStickyBar = pastHero && !atFooter;
  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Seo
        title="Glowie: Your AI Skincare Chemist"
        description="Glowie is your AI skincare chemist. Get a personalized routine, decode any ingredient, formulate your own products, and roast your shelf in seconds. Free to start."
        canonical="/"
        imageAlt="Glowie, your AI skincare chemist"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What is Glowie?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Glowie is an AI skincare app powered by an AI chemist that builds routines tailored to your skin type, decodes ingredients, and helps you formulate your own products at home.",
              },
            },
            {
              "@type": "Question",
              name: "Is it free to use?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. New users get free credits to chat with Glowie, run a skin analysis, and roast their shelf. You only top up if you want more.",
              },
            },
            {
              "@type": "Question",
              name: "Can it help with specific concerns like acne or hyperpigmentation?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Glowie tailors recommendations to acne, redness, hyperpigmentation, dryness, sensitivity, and aging concerns based on your quiz answers and skin photo.",
              },
            },
            {
              "@type": "Question",
              name: "Does it work for men?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Absolutely. Pick your hype energy in the quiz and Glowie adapts her tone, vocabulary, and product picks accordingly, including masculine-coded brands and unisex lines.",
              },
            },
          ],
        }}
      />
      {/* Decorative mesh blob */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[900px] bg-gradient-mesh -z-10" aria-hidden />

      <header className="container mx-auto px-4 sm:px-6 py-5 sm:py-6 flex items-center justify-between gap-3 relative z-10">
        <Link to="/" className="flex items-center gap-2 sm:gap-3 group min-w-0">
          <img src={stickerStar} alt="" className="h-9 w-9 sm:h-12 sm:w-12 lg:h-14 lg:w-14 wiggle-on-hover shrink-0" />
          <span className="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold tracking-tight whitespace-nowrap leading-none">Glowie</span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3">
          {user ? (
            <>
              <Button asChild size="sm" className="sm:h-10 sm:px-5 rounded-full">
                <Link to={primaryAppPath}>
                  <span className="hidden sm:inline">Open the app</span>
                  <span className="sm:hidden">Open app</span>
                  <ArrowRight className="ml-1.5 sm:ml-2 h-4 w-4" />
                </Link>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Account menu"
                    className="rounded-full ring-offset-background transition-shadow hover:shadow-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <Avatar className="h-9 w-9 sm:h-10 sm:w-10 border border-border">
                      <AvatarFallback className="bg-primary/10 text-primary font-medium">
                        {initial}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{displayName}</span>
                      {user.email && (
                        <span className="text-xs text-muted-foreground truncate">
                          {user.email}
                        </span>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/app">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/app/account">
                      <UserIcon className="mr-2 h-4 w-4" />
                      Account
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/app/billing">
                      <CreditCard className="mr-2 h-4 w-4" />
                      Billing
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => signOut()}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" className="sm:h-10 sm:px-4 rounded-full">
                <Link to="/auth">Sign in</Link>
              </Button>
              <Button asChild size="sm" className="sm:h-10 sm:px-5 rounded-full shadow-glow whitespace-nowrap">
                <Link to="/auth?mode=signup" aria-label="Get started with Glowie, sign up free">
                  <span className="hidden sm:inline">Get started</span>
                  <span className="sm:hidden">Start</span>
                </Link>
              </Button>
            </>
          )}
        </nav>
      </header>

      <main>
      {/* HERO */}
      <section ref={heroRef} className="container mx-auto px-4 sm:px-6 pt-8 sm:pt-16 pb-16 sm:pb-24 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 relative z-10 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-card border border-border text-xs text-foreground mb-6 shadow-soft">
              <Bot className="h-3.5 w-3.5 text-primary" />
              <span className="h-1.5 w-1.5 rounded-full bg-primary twinkle" />
              Your AI skincare bestie
            </div>
            <h1 className="font-display text-[2.75rem] sm:text-6xl md:text-7xl lg:text-[5rem] font-semibold tracking-tight leading-[1.02] mb-6 text-balance">
              Your skin, taken <span className="italic text-primary">seriously</span>. Without the{" "}
              <span className="italic relative inline-block">
                lab coat
                <svg className="absolute -bottom-1 left-0 w-full" height="14" viewBox="0 0 200 14" fill="none" preserveAspectRatio="none">
                  <path d="M2 9 Q 60 2, 100 7 T 198 6" stroke="hsl(var(--primary))" strokeWidth="3" strokeLinecap="round" fill="none" />
                </svg>
              </span>.
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mb-8 max-w-xl leading-relaxed">
              Your AI skincare chemist who actually reads the label. Build a routine, decode any product, or formulate your own serums and masks at home. First chat free.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-5">
              <Button asChild size="lg" className="w-full sm:w-auto rounded-full h-12 px-7 shadow-glow">
                <Link to={ctaPath}>
                  {ctaLabel} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-card border border-border text-xs text-foreground shadow-soft self-center sm:self-auto">
                <img src={stickerStar} alt="" className="h-5 w-5" />
                <span><span className="font-semibold">1 free chat</span> every day ✨</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
              <span>No subscriptions. Honest answers. Real ingredients.</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-elegant bg-secondary">
              <img
                src={heroPortrait}
                alt="Glowing dewy skin under warm light"
                className="absolute inset-0 h-full w-full object-cover"
                width={1080}
                height={1350}
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/10 to-transparent" />
            </div>

            {/* Floating polaroid */}
            <div className="absolute left-2 sm:-left-6 lg:-left-10 top-6 sm:top-10 rotate-[-8deg] bg-card p-2 pb-8 rounded-md shadow-elegant hidden sm:block float-slow">
              <img src={flatlay} alt="" loading="lazy" className="h-24 w-24 lg:h-28 lg:w-28 object-cover" />
              <p className="absolute bottom-2 left-0 right-0 text-center font-display italic text-xs text-muted-foreground">
                today's haul
              </p>
            </div>

            {/* Floating heart */}
            <img
              src={stickerHeart}
              alt=""
              className="absolute -bottom-4 -left-4 h-16 w-16 rotate-[-15deg] float-slow hidden sm:block"
            />

            {/* Mini quote bubble */}
            <div className="absolute -bottom-4 right-4 sm:right-6 max-w-[220px] rotate-[4deg] bg-card border border-border rounded-2xl rounded-bl-sm px-4 py-3 shadow-soft">
              <p className="text-xs italic font-display">
                "skip the niacinamide tonight, your barrier needs a break 💧"
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">Glowie, reading your routine</p>
            </div>
          </div>
        </div>
      </section>

      {/* GLOWIE QUOTES — texts from Glowie (moved up: directly after hero) */}
      <section className="container mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-2xl mb-10 sm:mb-14">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary">/ Texts from Glowie</span>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 leading-[1.05] text-balance">
            How Glowie actually <span className="italic text-primary">talks</span> to you.
          </h2>
          <p className="text-muted-foreground mt-3 text-sm sm:text-base">
            Smart, warm, never preachy. The friend who actually read the ingredient list.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {glowieQuotes.map((q, i) => (
            <div
              key={i}
              className="crisp-quote-bubble bg-card border border-border rounded-3xl rounded-bl-md px-5 py-4 shadow-soft"
            >
              <p className="text-sm sm:text-base font-display italic leading-snug text-foreground">
                "{q.q}"
              </p>
              <p className="text-xs text-muted-foreground mt-2">{q.ctx}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REAL FORMULA DEMO — show-don't-tell for the formulation feature.
          NOTE: the formula content below is a PLACEHOLDER. Swap with a real
          Glowie-generated formula when available. */}
      <section className="container mx-auto px-4 sm:px-6 pb-4 sm:pb-8">
        <div className="max-w-2xl mb-8 sm:mb-10">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary">/ A real formula from Glowie</span>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 leading-[1.05] text-balance">
            This is what a Glowie formula <span className="italic text-primary">actually</span> looks like.
          </h2>
          <p className="text-muted-foreground mt-3 text-sm sm:text-base">
            Not vague advice. An actual recipe with ingredients, ratios, and safety notes.
          </p>
        </div>
        {/* PLACEHOLDER FORMULA CARD — replace contents with a real Glowie formula later */}
        <div className="max-w-2xl rounded-3xl border border-border bg-gradient-cream/60 p-6 sm:p-8 shadow-card relative overflow-hidden">
          <div className="absolute -top-6 -right-6 opacity-60 pointer-events-none">
            <img src={stickerStar} alt="" className="h-20 w-20 rotate-12" />
          </div>
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground mb-3">
            For: dehydrated skin with occasional breakouts
          </p>
          <h3 className="font-display text-2xl sm:text-3xl font-semibold leading-tight mb-5">
            5% Niacinamide + 1% Salicylic Toner
          </h3>
          <div className="mb-5">
            <p className="text-sm font-semibold text-foreground mb-2">Ingredients</p>
            <ul className="space-y-1.5 text-sm text-foreground/90">
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />90 ml distilled water or rose hydrosol</li>
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />5 g niacinamide powder</li>
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />10 ml salicylic acid 1% solution</li>
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />0.5 g preservative (Geogard ECT)</li>
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />2-3 drops glycerin</li>
            </ul>
          </div>
          <div className="mb-5">
            <p className="text-sm font-semibold text-foreground mb-2">Method</p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Mix in a clean glass bottle. Target pH 5.0-5.5. Patch test 24 hours before facial use. Store refrigerated, lasts ~3 months.
            </p>
          </div>
          <div className="border-t border-border/70 pt-4 flex gap-3 items-start">
            <Sparkles className="h-4 w-4 text-primary shrink-0 mt-1" />
            <p className="text-sm font-display italic text-foreground/90 leading-relaxed">
              Glowie's note: "I'd start with this twice a week and see how your skin reacts. We can dial it up from there."
            </p>
          </div>
        </div>
      </section>

      {/* MOAT STATEMENT — pure positioning, no CTA */}
      <section className="container mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="rounded-3xl bg-primary/15 border border-primary/20 px-6 sm:px-12 py-10 sm:py-14 text-center">
          <p className="font-display text-2xl sm:text-3xl md:text-4xl leading-snug text-foreground text-balance max-w-3xl mx-auto">
            Most AI skincare apps tell you what to buy. <span className="italic text-primary">Glowie helps you make it.</span> From a real ingredient library, with real ratios, with real safety notes, at home, on your counter.
          </p>
        </div>
      </section>

      {/* INLINE CTA after Texts */}
      <section className="container mx-auto px-4 sm:px-6 pb-16 sm:pb-20">
        <div className="max-w-2xl mx-auto text-center">
          <p className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-5 leading-tight text-balance">
            Want Glowie to talk to you like this?
          </p>
          <Button asChild size="lg" className="rounded-full h-12 px-7 shadow-glow">
            <Link to={ctaPath}>
              Try it free <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* SOCIAL PROOF — early users */}
      <section className="bg-gradient-cream/40 border-y border-border">
        <div className="container mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="max-w-2xl mb-10 sm:mb-14">
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary">/ Loved by early users</span>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 leading-[1.05] text-balance">
              Early users are <span className="italic text-primary">obsessed</span>.
            </h2>
          </div>
          {/* Mobile: horizontal scroll. Desktop: grid */}
          <div className="flex md:grid md:grid-cols-3 gap-4 sm:gap-5 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0 pb-2 md:pb-0">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="snap-start shrink-0 w-[85%] md:w-auto rounded-3xl border border-border bg-card p-6 sm:p-7 shadow-card hover:shadow-elegant transition-all flex flex-col"
              >
                <div className="flex gap-1 mb-3 text-primary">
                  {[0,1,2,3,4].map((s) => (
                    <Star key={s} className="h-4 w-4 fill-primary" />
                  ))}
                </div>
                <p className="font-display italic text-base sm:text-lg leading-snug text-foreground mb-4 flex-1">
                  "{t.q}"
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{t.name}</span>, {t.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOUND FAMILIAR — problem statement */}
      <section className="container mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary">/ The reality</span>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 leading-[1.05] text-balance">
              Sound <span className="italic text-primary">familiar</span>?
            </h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-4 sm:gap-5 mb-10">
            {painPoints.map((p) => (
              <li
                key={p}
                className="rounded-2xl border border-border bg-card px-5 py-4 shadow-soft flex gap-3 items-start"
              >
                <span className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" aria-hidden />
                <span className="text-sm sm:text-base text-foreground leading-snug">{p}</span>
              </li>
            ))}
          </ul>
          <p className="font-display text-xl sm:text-2xl italic text-center text-foreground mb-6 text-balance">
            Glowie is the friend who actually reads the ingredient list.
          </p>
          <div className="flex justify-center">
            <Button asChild size="lg" className="rounded-full h-12 px-7 shadow-glow">
              <Link to={ctaPath}>
                Try it free <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="container mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-2xl mb-10 sm:mb-14">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary">/ Why she's different</span>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 leading-[1.05] text-balance">
            Less guesswork, more <span className="italic text-primary">glow</span>.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="group relative rounded-3xl border border-border bg-card p-6 sm:p-8 shadow-card hover:shadow-elegant transition-all overflow-hidden"
            >
              <div className="flex gap-5 sm:gap-6">
                <div className="shrink-0">
                  <img
                    src={f.accent}
                    alt=""
                    loading="lazy"
                    className={`h-20 w-20 sm:h-28 sm:w-28 rounded-2xl object-cover shadow-soft transition-transform group-hover:scale-105 ${
                      i % 2 === 0 ? "rotate-[-3deg]" : "rotate-[3deg]"
                    }`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary mb-2">
                    {f.eyebrow}
                  </div>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold mb-2 leading-tight">
                    {f.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INLINE CTA between features and how-it-works */}
      <section className="container mx-auto px-4 sm:px-6 pb-16 sm:pb-20 -mt-4 sm:-mt-8">
        <div className="flex justify-center">
          <Button asChild size="lg" className="rounded-full h-12 px-7 shadow-glow">
            <Link to={ctaPath}>
              Try it free <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* HOW THE FORMULA FINDER WORKS */}
      <section className="container mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-2xl mb-10 sm:mb-14">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary">/ How the formula finder works</span>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 leading-[1.05] text-balance">
            From your goals to a real <span className="italic text-primary">routine</span>.
          </h2>
          <p className="text-muted-foreground mt-3 text-sm sm:text-base">
            Glowie pairs what you tell her with a deep ingredient library, then builds a routine you can actually follow.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {[
            {
              n: "01",
              t: "You share your goals",
              d: "Skin type, concerns, budget, lifestyle, and whether you lean natural, synthetic, or both. Takes about a minute.",
            },
            {
              n: "02",
              t: "She reads the ingredients",
              d: "Glowie cross-references a library of actives, carriers, and brand formulas, including what each ingredient does, who it suits, and what it clashes with.",
            },
            {
              n: "03",
              t: "She matches to your skin",
              d: "Your goals get mapped to ingredients that target them, then to either real products you can buy OR a DIY formula you can mix at home, whichever fits your life.",
            },
            {
              n: "04",
              t: "You get a real routine",
              d: "A clear AM and PM plan with order, frequency, and reasons, so you know why each step is there and can swap pieces anytime. And if you'd rather make a piece of your routine yourself, Glowie hands you the formula step by step.",
            },
          ].map((s) => (
            <div
              key={s.n}
              className="rounded-3xl border border-border bg-card p-6 shadow-card hover:shadow-elegant transition-all"
            >
              <div className="font-display text-4xl font-semibold text-primary/70 mb-3">{s.n}</div>
              <h3 className="font-display text-xl font-semibold mb-2 leading-tight">{s.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <Button asChild size="lg" className="rounded-full h-12 px-7 shadow-glow">
            <Link to={ctaPath}>
              Try it free <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* FINAL HERO REPEAT */}
      <section className="container mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto items-center">
          <div className="relative">
            <img
              src={glow}
              alt="A close-up smile, glowing skin"
              loading="lazy"
              className="rounded-[2rem] aspect-square object-cover w-full shadow-elegant"
            />
            <img src={stickerStar} alt="" className="absolute -top-6 -right-4 h-16 w-16 wiggle-on-hover" />
          </div>
          <div>
            <Heart className="h-6 w-6 text-primary mb-4 fill-primary/30" />
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-4 leading-tight text-balance">
              Your skin, taken seriously. Without the lab coat.
            </h2>
            <p className="text-muted-foreground mb-6">
              First chat is on us. Then pay-as-you-go credits, no subscriptions, no nonsense.
            </p>
            <Button asChild size="lg" className="rounded-full h-12 px-7 shadow-glow">
              <Link to={ctaPath}>
                {ctaLabel} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      </main>

      <footer ref={footerRef} className="border-t border-border py-10 text-center text-xs text-muted-foreground">
        <div className="container mx-auto px-4 sm:px-6 flex flex-col items-center gap-5">
          <div className="flex items-center justify-center gap-2">
            <img src={stickerStar} alt="" className="h-5 w-5" />
            <span className="font-display text-base font-semibold text-foreground">Glowie</span>
          </div>
          <p className="max-w-xl">
            Friendly skincare info, not medical advice. Always patch-test new products. Made with care.
          </p>
          <SocialShareRow
            url="https://meetglowie.com"
            title="Glowie, your AI skincare bestie"
            text="Found my new skincare bestie. Glowie roasts my shelf, scores my glow, and writes routines that actually work."
            label="Tell a friend"
            surface="landing-footer"
          />
        </div>
      </footer>

      {/* Sticky mobile CTA bar — hidden inside hero, hidden at footer, visible everywhere else */}
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 z-40 transition-transform duration-300 ${
          showStickyBar ? "translate-y-0" : "translate-y-full"
        }`}
        aria-hidden={!showStickyBar}
      >
        <div className="bg-card/95 backdrop-blur border-t border-border shadow-elegant px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <img src={stickerStar} alt="" className="h-7 w-7 shrink-0" />
            <div className="text-xs leading-tight min-w-0">
              <div className="font-display font-semibold text-foreground">Glowie</div>
              <div className="text-muted-foreground">Free to try</div>
            </div>
          </div>
          <Button asChild size="sm" className="rounded-full h-10 px-5 shadow-glow shrink-0">
            <Link to={ctaPath}>
              Try it free <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Index;
